import * as React from 'react';
import { View, TextInput, Pressable, TouchableOpacity, StyleSheet, KeyboardAvoidingView, Platform, Alert, ActivityIndicator } from 'react-native';
import { Text } from '@/components';
import { useRouter } from 'expo-router';
import * as Haptics from 'expo-haptics';
import * as AuthSession from 'expo-auth-session';
import { useAuth } from '@/context/AuthContext';
import { firebaseAuthExchange } from '@/lib/api';
import * as WebBrowser from 'expo-web-browser';
import * as Google from 'expo-auth-session/providers/google';
import * as AppleAuthentication from 'expo-apple-authentication';
import { Apple } from 'lucide-react-native';
import Constants from 'expo-constants';
import { Image } from 'expo-image';
import theme from '@/constants/colors';
import { getFirebaseAuth, signInWithEmailAndPassword, signInWithGoogle, signInWithApple } from '@/lib/firebase';
import { logEvent } from '@/lib/analytics';
import styles from './auth.styles';
import { useTranslation } from 'react-i18next';

import AppleSvg from '../assets/images/Apple.svg';
import GoogleSvg from '../assets/images/Google.svg';

WebBrowser.maybeCompleteAuthSession();

export default function LoginScreen() {
  const router = useRouter();
  const { signInWithToken } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = React.useState<string>('');
  const [password, setPassword] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  // HARDCODED: No logic, just the exact strings from your Google Cloud Console
  const GOOGLE_IOS_CLIENT_ID = "447944956309-t3me1erabpnf99e12gsc8ogf9mt84dtj.apps.googleusercontent.com";
  const REVERSED_CLIENT_ID = "com.googleusercontent.apps.447944956309-t3me1erabpnf99e12gsc8ogf9mt84dtj";
  const GOOGLE_ANDROID_CLIENT_ID = "447944956309-2nbcukm9lvh5gi6hjaikpjb7khvl12vb.apps.googleusercontent.com";
  
  const redirectUri = AuthSession.makeRedirectUri({
    // This 'scheme' property tells Expo: "Ignore the other 3 schemes, use THIS one."
    scheme: Platform.OS === 'ios' 
      ? REVERSED_CLIENT_ID 
      : 'com.tailtrailsclub.app',
    path: '', 
  });

  console.log("REDIRECT CHECK:", redirectUri);
  // Should look like: com.tailtrailsclub.app://oauth2redirect/google

  const [request, response, promptAsync] = Google.useAuthRequest({
    iosClientId: GOOGLE_IOS_CLIENT_ID,
    webClientId: "447944956309-t92ug0tqs40adop6mnvifqbmfsi7dtj6.apps.googleusercontent.com",
    androidClientId: GOOGLE_ANDROID_CLIENT_ID,
    scopes: ['openid', 'profile', 'email'],
    responseType: 'code',
    redirectUri: redirectUri,
  });

  React.useEffect(() => {
    (async () => {
      if (response?.type === 'success') {
        try {
          setIsLoading(true);

          // LOG THIS to see what you're actually getting
          console.log('Full Response:', JSON.stringify(response, null, 2));

          // Priority 1: idToken from the authentication object
          // Priority 2: id_token from the params
          const idToken = response.authentication?.idToken || response.params?.id_token;

          if (!idToken) {
            throw new Error('Google did not return an ID Token. Check your scopes.');
          }

          // Pass ONLY the idToken to your firebase function
          const fb = await signInWithGoogle(getFirebaseAuth(), idToken);

          const session = await firebaseAuthExchange(fb.idToken);
          await signInWithToken(session);
          logEvent('login', { method: 'google' });

          Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
          router.replace('/');
        } catch (err: any) {
          console.error('Google sign-in error', err);
          Alert.alert(t('auth.loginError'), err?.message);
        } finally {
          setIsLoading(false);
        }
      }
    })();
  }, [response]);

  const handleSignIn = async () => {
    console.log('handleSignIn invoked', { email, password });
    if (!email || !password) {
      if (true) {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
      }
      Alert.alert(t('common.error'), t('auth.pleaseEnterEmailPassword'));
      return;
    }

    if (true) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    }

    setIsLoading(true);
    try {
      const credential = await signInWithEmailAndPassword(
        getFirebaseAuth(),
        email.trim().toLowerCase(),
        password
      );
      const idToken = await credential.user.getIdToken();
      const session = await firebaseAuthExchange(idToken);
      await signInWithToken(session);
      logEvent('login', { method: 'email' });
      if (true) {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      }
      router.replace('/');
    } catch (error: any) {
      if (true) {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
      }
      console.error('Firebase sign-in error:', error);
      Alert.alert(t('common.error'), error.message || t('auth.failedToSignIn'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGooglePress = async () => {
    if (!request) {
      Alert.alert(t('auth.googleSignInNotAvailable'));
      return;
    }
    try {
      // THE FIX: Explicitly tell the prompt NOT to use the proxy
      // This bypasses the "Must use http/https" internal validator
      await promptAsync();
    } catch (err: any) {
      console.error('Google prompt error', err);
      Alert.alert(t('auth.googleSignInError'), err?.message || String(err));
    }
  };

  const handleApplePress = async () => {
    if (isLoading) return;
    if (true) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    }
    try {
      setIsLoading(true);
      const available = await (AppleAuthentication.isAvailableAsync ? AppleAuthentication.isAvailableAsync() : Promise.resolve(false));
      if (!available) {
        Alert.alert(t('auth.appleSignInNotAvailable'));
        return;
      }

      const credential = await AppleAuthentication.signInAsync({
        requestedScopes: [
          AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
          AppleAuthentication.AppleAuthenticationScope.EMAIL,
        ],
      });

      const idToken = (credential as any)?.identityToken;
      if (!idToken) throw new Error('Apple did not return an identity token.');

      const fb = await signInWithApple(getFirebaseAuth(), idToken);
      const session = await firebaseAuthExchange(fb.idToken);
      await signInWithToken(session);
      logEvent('login', { method: 'apple' });

      if (true) {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      }
      router.replace('/');
    } catch (err: any) {
      console.error('Apple sign-in error:', err);
      Alert.alert(t('auth.appleSignInError'), err?.message || String(err));
      if (true) {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const webKeyProps: any = typeof window !== 'undefined'
    ? {
      onKeyDown: (e: any) => {
        const k = e?.nativeEvent?.key || e?.key;
        if (k === 'Enter') {
          handleSignIn();
        }
      },
    }
    : {};

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <View style={styles.content}>
        <View style={styles.header}>
          <Image
            source={{ uri: 'https://api.tailtrails.club/assets/logo' }}
            style={styles.logo}
            contentFit="contain"
          />
          <Text style={styles.title}>{t('auth.welcomeBack')}</Text>
          <Text style={styles.subtitle}>{t('auth.signInToTrack')}</Text>
        </View>

        <View style={styles.form}>
          <View style={styles.inputContainer}>
            <Text style={styles.label}>{t('auth.email')}</Text>
            <TextInput
              style={styles.input}
              placeholder="your@email.com"
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              keyboardType="email-address"
              autoComplete="email"
              editable={!isLoading}
              placeholderTextColor={theme.textMuted}
            />
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>{t('auth.password')}</Text>
            <TextInput
              style={styles.input}
              placeholder={t('auth.enterPassword')}
              value={password}
              onChangeText={setPassword}
              secureTextEntry
              autoCapitalize="none"
              autoComplete="password"
              editable={!isLoading}
              placeholderTextColor={theme.textMuted}
            />
          </View>

          <Pressable
            style={({ pressed }) => [styles.button, isLoading && styles.buttonDisabled, pressed && styles.buttonPressed]}
            onPress={handleSignIn}
            {...webKeyProps}
            accessibilityRole="button"
            disabled={isLoading}
          >
            {isLoading ? (
              <ActivityIndicator color={theme.backgroundPrimary} />
            ) : (
              <Text style={styles.buttonText}>{t('auth.signIn')}</Text>
            )}
          </Pressable>

          <View style={styles.socialRow}>
            <TouchableOpacity
              style={styles.socialButton}
              onPress={handleGooglePress}
              disabled={!request || isLoading}
            >
              <GoogleSvg style={styles.socialIcon} preserveAspectRatio="xMidYMid meet" />
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.socialButton}
              onPress={async () => handleApplePress()}
              disabled={isLoading}
            >
              <AppleSvg style={styles.socialIcon} preserveAspectRatio="xMidYMid meet" />
            </TouchableOpacity>
          </View>

          {/* ... */}

          <View style={styles.footer}>
            <Text style={styles.footerText}>{t('auth.dontHaveAccount')}</Text>
            <TouchableOpacity onPress={() => {
              if (true) {
                Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
              }
              router.push('/signup');
            }}>
              <Text style={styles.linkText}>{t('auth.signUp')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </KeyboardAvoidingView>
  );
}